import config from '../config';
import { applyDevImpersonationHeader } from '../auth/devImpersonation';

const jsonContentType = 'application/json';
let accessTokenProvider = null;
let originalFetch = null;

export const setAccessTokenProvider = (provider) => {
    accessTokenProvider = provider;
};

const isBackendUrl = (url) => typeof url === 'string' && url.startsWith(config.baseResource);

export const installAuthenticatedFetch = () => {
    if (typeof window === 'undefined' || originalFetch) {
        return;
    }

    originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
        const requestUrl = typeof input === 'string' ? input : input?.url;

        if (!isBackendUrl(requestUrl)) {
            return originalFetch(input, init);
        }

        const requestHeaders = applyDevImpersonationHeader(new Headers(init.headers || input?.headers));

        if (!requestHeaders.has('Authorization')) {
            const accessToken = await resolveAccessToken();

            if (accessToken) {
                requestHeaders.set('Authorization', `Bearer ${accessToken}`);
            }
        }

        return originalFetch(input, {
            ...init,
            headers: requestHeaders
        });
    };
};

export const uninstallAuthenticatedFetch = () => {
    if (typeof window === 'undefined' || !originalFetch) {
        return;
    }

    window.fetch = originalFetch;
    originalFetch = null;
};

export class ApiError extends Error {
    constructor(message, { status, body } = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

const trimSlashes = (value) => value.replace(/^\/+|\/+$/g, '');

export const buildApiUrl = (path) => {
    if (!path) {
        return config.baseResource;
    }

    return `${config.baseResource}/${trimSlashes(path)}`;
};

const parseResponseBody = async (response) => {
    if (response.status === 204) {
        return null;
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes(jsonContentType)) {
        return response.json();
    }

    return response.text();
};

const resolveAccessToken = async (token) => {
    if (token) {
        return token;
    }

    if (!accessTokenProvider) {
        return null;
    }

    return accessTokenProvider();
};

export const apiRequest = async (path, { method = 'GET', body, headers = {}, token, authenticated = true } = {}) => {
    const requestHeaders = applyDevImpersonationHeader(new Headers(headers));
    const accessToken = authenticated && !requestHeaders.has('Authorization')
        ? await resolveAccessToken(token)
        : token;

    if (body !== undefined && !requestHeaders.has('Content-Type')) {
        requestHeaders.set('Content-Type', jsonContentType);
    }

    if (accessToken && !requestHeaders.has('Authorization')) {
        requestHeaders.set('Authorization', `Bearer ${accessToken}`);
    }

    const response = await fetch(buildApiUrl(path), {
        method,
        headers: requestHeaders,
        body: body === undefined ? undefined : JSON.stringify(body)
    });

    const responseBody = await parseResponseBody(response);

    if (!response.ok) {
        throw new ApiError(`API request failed with status ${response.status}`, {
            status: response.status,
            body: responseBody
        });
    }

    return responseBody;
};

export const apiGet = (path, options) => apiRequest(path, { ...options, method: 'GET' });

export const apiPost = (path, body, options) => apiRequest(path, { ...options, method: 'POST', body });

export const apiPut = (path, body, options) => apiRequest(path, { ...options, method: 'PUT', body });

export const apiDelete = (path, options) => apiRequest(path, { ...options, method: 'DELETE' });
export default class httpClient {
}